import { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SapiensAuthClient, ALLOWED_ORIGINS } from '@sapiens/auth-core';
import { authApi } from '@/api';
import { AuthLayout } from '@/components/layout';
import sapiensLogo from '@/image/blue-sapiens-logo.svg';

export function SsoLoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const targetOrigin = searchParams.get('targetOrigin') || '';
  const state = searchParams.get('state') || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Validate origin
  const isValidOrigin = useMemo(() => {
    return ALLOWED_ORIGINS.includes(targetOrigin);
  }, [targetOrigin]);

  // Check if we're in a popup
  const isPopup = useMemo(() => {
    return window.opener !== null;
  }, []);

  // Open a page in a new window (for recovery/activation)
  const openInNewWindow = (path: string) => {
    const width = 500;
    const height = 650;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      `/#${path}`,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  // Handle cancel
  const handleCancel = () => {
    if (isPopup && isValidOrigin) {
      SapiensAuthClient.sendSSOCancel(state, targetOrigin);
    }
  };

  // Close window on error if not popup
  useEffect(() => {
    if (!isValidOrigin && !targetOrigin) {
      // No targetOrigin provided - just show login page normally
      return;
    }
  }, [isValidOrigin, targetOrigin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.login({ email, password });

      if (!response.success || !response.data) {
        throw new Error(response.message || t('auth.loginError'));
      }

      const { accessToken, expiresIn, user } = response.data;
      const expiresAt = Date.now() + expiresIn * 1000;

      setSuccess(true);

      // If in popup with valid origin, send message to opener
      if (isPopup && isValidOrigin) {
        // Small delay to show success message
        setTimeout(() => {
          SapiensAuthClient.sendSSOSuccess(
            accessToken,
            user,
            expiresAt,
            state,
            targetOrigin
          );
        }, 500);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.loginError');
      setError(message);

      // Send error to opener if in popup
      if (isPopup && isValidOrigin) {
        SapiensAuthClient.sendSSOError(message, state, targetOrigin);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show error if invalid origin
  if (targetOrigin && !isValidOrigin) {
    return (
      <AuthLayout title={t('sso.title')}>
        <Alert severity="error">{t('sso.invalidOrigin')}</Alert>
        <Typography variant="body2" color="text.secondary" mt={2}>
          Origin: {targetOrigin}
        </Typography>
      </AuthLayout>
    );
  }

  // Show success message
  if (success) {
    return (
      <AuthLayout title={t('sso.title')}>
        <Alert severity="success">{t('sso.loginSuccess')}</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('sso.title')}
      subtitle={t('sso.description')}
      logo={sapiensLogo}
      logoAlt="Sapiens Logo"
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t('auth.email')}
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('auth.password')}
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />

        <FormControlLabel
          control={
            <Checkbox
              value="remember"
              color="primary"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
            />
          }
          label={t('auth.rememberMe')}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading || !email || !password}
        >
          {loading ? t('common.loading') : t('auth.login')}
        </Button>

        {isPopup && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            onClick={() => openInNewWindow('/recovery')}
            sx={{ cursor: 'pointer' }}
          >
            {t('auth.forgotPassword')}
          </MuiLink>
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            onClick={() => openInNewWindow('/activation')}
            sx={{ cursor: 'pointer' }}
          >
            {t('activation.title')}
          </MuiLink>
        </Box>
      </Box>
    </AuthLayout>
  );
}
